import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { agentCreateFormSchema, agentUpdateFormSchema } from "../../lib/validators";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Select } from "../ui/Select";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";

export type AgentFormValues = z.infer<typeof agentUpdateFormSchema>;

interface AgentFormProps {
  defaultValues?: Partial<AgentFormValues>;
  onSubmit: (values: AgentFormValues) => Promise<void>;
  mode: "create" | "update";
}

const roleOptions: Array<{ value: AgentFormValues["role"]; label: string }> = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "agent", label: "Agent" },
  { value: "viewer", label: "Viewer" }
];

export const AgentForm = ({ defaultValues, onSubmit, mode }: AgentFormProps) => {
  const schema = mode === "create" ? agentCreateFormSchema : agentUpdateFormSchema;
  const getMessage = (value: unknown) => (typeof value === "string" ? value : undefined);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<AgentFormValues>({
    resolver: zodResolver(schema),
    defaultValues
  });

  const handleFormSubmit = async (values: AgentFormValues) => {
    await onSubmit(values);
    reset({ ...values, password: "", confirmPassword: "" });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Agent name</Label>
          <Input id="name" placeholder="Taylor Swift" {...register("name")} />
          {getMessage(errors.name?.message) ? <p className="text-xs text-red-400">{getMessage(errors.name?.message)}</p> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" placeholder="agent@rwasupport.io" {...register("email")} />
          {getMessage(errors.email?.message) ? <p className="text-xs text-red-400">{getMessage(errors.email?.message)}</p> : null}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="role">Role</Label>
        <Select id="role" {...register("role")}>
          {roleOptions.map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </Select>
        {getMessage(errors.role?.message) ? <p className="text-xs text-red-400">{getMessage(errors.role?.message)}</p> : null}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="password">
            Password {mode === "update" ? <span className="text-slate-500">(optional)</span> : null}
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="Strong passphrase"
            {...register("password")}
          />
          {getMessage(errors.password?.message) ? <p className="text-xs text-red-400">{getMessage(errors.password?.message)}</p> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Confirm passphrase"
            {...register("confirmPassword")}
          />
          {getMessage(errors.confirmPassword?.message) ? (
            <p className="text-xs text-red-400">{getMessage(errors.confirmPassword?.message)}</p>
          ) : null}
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Spinner size="sm" /> Saving
          </>
        ) : mode === "create" ? (
          "Create agent"
        ) : (
          "Update agent"
        )}
      </Button>
    </form>
  );
};

